/**
 * Реестр локалей УЛЬЯ: 20 языков, их направление письма, шрифт и волна запуска.
 *
 * Назначение файла: одно место, где описано «какие языки существуют и что про них
 * нужно знать интерфейсу». Из него берутся: список в настройках, направление RTL,
 * подгружаемый шрифт, фолбэк и признак готовности перевода.
 *
 * Импортов нет: чистые данные, чтобы файл можно было использовать и в сборщике
 * (для сабсетов шрифтов), и на сервере (для пушей).
 *
 * Источник: docs/19-localization.md §19.2, §19.9, §19.10
 */

/** Код локали в формате BCP 47. Ровно в таком написании, включая регион у pt-BR. */
export type LocaleCode =
  | "ru"
  | "en"
  | "es"
  | "pt-BR"
  | "de"
  | "fr"
  | "it"
  | "pl"
  | "tr"
  | "uk"
  | "nl"
  | "ar"
  | "ja"
  | "ko"
  | "zh-Hans"
  | "zh-Hant"
  | "id"
  | "th"
  | "vi"
  | "hi";

/** Направление письма. Влияет на атрибут dir и на зеркалирование интерфейса. */
export type TextDirection = "ltr" | "rtl";

/** Группа шрифтов: определяет, какой файл шрифта подгружать для локали (§19.10). */
export type FontGroup =
  | "base" // латиница + кириллица, входит в сборку
  | "arabic"
  | "cjk-sc"
  | "cjk-tc"
  | "cjk-jp"
  | "cjk-kr"
  | "thai"
  | "devanagari";

/** Волна запуска локали (§19.2): 0 — русский, 4 — после релиза. */
export type LaunchWave = 0 | 1 | 2 | 3 | 4;

export interface LocaleMeta {
  readonly code: LocaleCode;
  /** Название языка на самом языке: в переключателе пишем «Deutsch», а не «Немецкий». */
  readonly nativeName: string;
  readonly direction: TextDirection;
  readonly font: FontGroup;
  readonly wave: LaunchWave;
  /** Система измерений по умолчанию: у США мили, у остальных метры (§19.8). */
  readonly measurement: "metric" | "imperial";
}

/**
 * Все 20 локалей. Порядок — по волнам, внутри волны по коду:
 * так список в настройках выглядит предсказуемо, а перевод добавляется без перестановок.
 *
 * i18n-ignore-start: самоназвания языков не переводятся никогда. «Русский» остаётся
 * «Русским» в английском интерфейсе, иначе игрок не найдёт свой язык в списке (§19.7).
 */
export const LOCALES: ReadonlyArray<LocaleMeta> = [
  // Волна 0 — авторский язык
  {
    code: "ru",
    nativeName: "Русский",
    direction: "ltr",
    font: "base",
    wave: 0,
    measurement: "metric",
  },

  // Волна 1 — pivot для всех остальных переводов
  {
    code: "en",
    nativeName: "English",
    direction: "ltr",
    font: "base",
    wave: 1,
    measurement: "metric",
  },

  // Волна 2 — Европа и Латинская Америка
  {
    code: "de",
    nativeName: "Deutsch",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },
  {
    code: "es",
    nativeName: "Español",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },
  {
    code: "fr",
    nativeName: "Français",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },
  {
    code: "it",
    nativeName: "Italiano",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },
  {
    code: "pl",
    nativeName: "Polski",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },
  {
    code: "pt-BR",
    nativeName: "Português (Brasil)",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },
  {
    code: "tr",
    nativeName: "Türkçe",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },
  {
    code: "uk",
    nativeName: "Українська",
    direction: "ltr",
    font: "base",
    wave: 2,
    measurement: "metric",
  },

  // Волна 3 — CJK и RTL: другие шрифты и другая вёрстка
  {
    code: "ar",
    nativeName: "العربية",
    direction: "rtl",
    font: "arabic",
    wave: 3,
    measurement: "metric",
  },
  {
    code: "ja",
    nativeName: "日本語",
    direction: "ltr",
    font: "cjk-jp",
    wave: 3,
    measurement: "metric",
  },
  {
    code: "ko",
    nativeName: "한국어",
    direction: "ltr",
    font: "cjk-kr",
    wave: 3,
    measurement: "metric",
  },
  {
    code: "zh-Hans",
    nativeName: "简体中文",
    direction: "ltr",
    font: "cjk-sc",
    wave: 3,
    measurement: "metric",
  },
  {
    code: "zh-Hant",
    nativeName: "繁體中文",
    direction: "ltr",
    font: "cjk-tc",
    wave: 3,
    measurement: "metric",
  },

  // Волна 4 — растущие рынки, добавляются по метрикам
  {
    code: "hi",
    nativeName: "हिन्दी",
    direction: "ltr",
    font: "devanagari",
    wave: 4,
    measurement: "metric",
  },
  {
    code: "id",
    nativeName: "Bahasa Indonesia",
    direction: "ltr",
    font: "base",
    wave: 4,
    measurement: "metric",
  },
  {
    code: "nl",
    nativeName: "Nederlands",
    direction: "ltr",
    font: "base",
    wave: 4,
    measurement: "metric",
  },
  { code: "th", nativeName: "ไทย", direction: "ltr", font: "thai", wave: 4, measurement: "metric" },
  {
    code: "vi",
    nativeName: "Tiếng Việt",
    direction: "ltr",
    font: "base",
    wave: 4,
    measurement: "metric",
  },
] as const;

/** Авторский язык: источник текстов. */
export const SOURCE_LOCALE: LocaleCode = "ru";

/** Второй фолбэк: если ключа нет ни в локали игрока, ни в русском — показываем английский. */
export const FALLBACK_LOCALE: LocaleCode = "en";

/**
 * Локали, перевод которых готов и которые видны игроку в настройках.
 * Пока это только русский: остальные появляются по волнам, чтобы игрок не наткнулся
 * на полупустой язык (§19.20, чеклист добавления языка).
 */
export const ENABLED_LOCALES: ReadonlyArray<LocaleCode> = ["ru"] as const;

/** Namespaces каталога: клиент подгружает только нужные экрану (§19.4). */
export const NAMESPACES = [
  "common",
  "onboarding",
  "map",
  "hex",
  "traces",
  "hero",
  "feed",
  "quests",
  "spells",
  "arena",
  "economy",
  "clan",
  "shop",
  "errors",
  "push",
  "mail",
  "legal",
  "support",
  "hearth",
  "traces",
] as const;

export type Namespace = (typeof NAMESPACES)[number];

const LOCALE_INDEX: ReadonlyMap<string, LocaleMeta> = new Map(
  LOCALES.map((locale) => [locale.code, locale]),
);

/** Метаданные локали по коду. Бросает исключение на неизвестном коде: молчать здесь опасно. */
export function localeMeta(code: LocaleCode): LocaleMeta {
  const meta = LOCALE_INDEX.get(code);
  if (!meta) throw new Error(`Неизвестная локаль: ${code}`);
  return meta;
}

/** Является ли строка поддерживаемым кодом локали. */
export function isLocaleCode(value: string): value is LocaleCode {
  return LOCALE_INDEX.has(value);
}

/**
 * Подбор локали по заголовку Accept-Language или по языку системы (§19.4).
 *
 * Логика: сначала ищем точное совпадение (`pt-BR`), затем совпадение по языку
 * (`pt` → `pt-BR`, `zh` → `zh-Hans`), затем отдаём русский. Учитываются только
 * включённые локали: предлагать игроку непереведённый язык хуже, чем дать русский.
 *
 * @param preferred список предпочтений в порядке убывания, например ["de-AT", "de", "en"]
 * @param enabled список доступных локалей, по умолчанию ENABLED_LOCALES
 */
export function resolveLocale(
  preferred: ReadonlyArray<string>,
  enabled: ReadonlyArray<LocaleCode> = ENABLED_LOCALES,
): LocaleCode {
  const enabledSet = new Set(enabled);

  for (const raw of preferred) {
    const tag = raw.trim();
    if (tag === "") continue;

    if (isLocaleCode(tag) && enabledSet.has(tag)) return tag;

    const language = tag.split("-")[0]?.toLowerCase();
    if (language === undefined) continue;

    const match = LOCALES.find(
      (locale) =>
        locale.code.split("-")[0]?.toLowerCase() === language && enabledSet.has(locale.code),
    );
    if (match) return match.code;
  }

  return SOURCE_LOCALE;
}

/** Направление письма локали: нужно для атрибута dir и логических CSS-свойств (§19.9). */
export function textDirection(code: LocaleCode): TextDirection {
  return localeMeta(code).direction;
}
